import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Moment } from 'moment'

import useFetchRefactored from 'civic-champs-shared/api/hooks/useFetchRefactored'
import { useCurrentOrg } from 'auth/hooks'
import format from 'civic-champs-shared/utils/format'
import { MESSAGES_FETCH_ALL_PAGE_SIZE } from 'messages/hooks/messagesServerSideSelectionConstants'
import { NotificationBlastSummary } from '../types'

export type CustomMessagesDateRange = {
  startDate: Moment | null
  endDate: Moment | null
} | null

export type CustomMessagesPagedSummary = {
  count: number
  emailCount: number
  smsCount: number
}

const DEFAULT_PAGE_SIZE = 25
const EMPTY_PAGED_SUMMARY: CustomMessagesPagedSummary = { count: 0, emailCount: 0, smsCount: 0 }

const buildFilterParams = ({ search, dateRange }: { search: string; dateRange: CustomMessagesDateRange }) => ({
  ...(search ? { search } : {}),
  ...(dateRange?.startDate ? { startDate: format.dateForUrl(dateRange.startDate) } : {}),
  ...(dateRange?.endDate ? { endDate: format.dateForUrl(dateRange.endDate) } : {}),
})

export const useFetchCustomMessages = (dateRange: CustomMessagesDateRange) => {
  const organization = useCurrentOrg()
  const [messages, setMessages] = useState<NotificationBlastSummary[]>([])
  const [totalCount, setTotalCount] = useState(0)
  const [pagedSummary, setPagedSummary] = useState<CustomMessagesPagedSummary>(EMPTY_PAGED_SUMMARY)
  const [loading, setLoading] = useState(true)
  const [pageIndex, setPageIndex] = useState(0)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [search, setSearch] = useState('')
  const [request] = useFetchRefactored()
  const searchRef = useRef('')
  const lastFilterKeyRef = useRef<string | null>(null)

  const filterKey = useMemo(() => JSON.stringify(buildFilterParams({ search, dateRange })), [search, dateRange])

  const refetch = useCallback(
    async ({ includeCount }: { includeCount?: boolean } = {}) => {
      setLoading(true)
      const filterParams = buildFilterParams({ search, dateRange })
      const shouldFetchCount = includeCount ?? lastFilterKeyRef.current !== filterKey

      if (shouldFetchCount) {
        lastFilterKeyRef.current = filterKey
      }

      const pageRequest = request({
        method: 'get',
        url: `/organizations/${organization.id}/notification-blasts-paged`,
        config: {
          queryStringParameters: {
            ...filterParams,
            limit: pageSize,
            offset: pageIndex * pageSize,
            sortBy: 'createdAt',
            sortDir: 'desc',
          },
        },
      }) as Promise<NotificationBlastSummary[]>

      try {
        if (shouldFetchCount) {
          const [summary, page] = await Promise.all([
            request({
              method: 'get',
              url: `/organizations/${organization.id}/notification-blasts-paged-count`,
              config: { queryStringParameters: filterParams },
            }) as Promise<CustomMessagesPagedSummary>,
            pageRequest,
          ])
          setTotalCount(summary.count)
          setPagedSummary(summary)
          setMessages(page)
        } else {
          setMessages(await pageRequest)
        }
      } catch {
        if (shouldFetchCount) {
          setTotalCount(0)
          setPagedSummary(EMPTY_PAGED_SUMMARY)
        }
        setMessages([])
      } finally {
        setLoading(false)
      }
    },
    [dateRange, filterKey, organization.id, pageIndex, pageSize, request, search],
  )

  const refetchWithCount = useCallback(() => refetch({ includeCount: true }), [refetch])

  useEffect(() => {
    refetch()
  }, [refetch])

  useEffect(() => {
    const maxPage = Math.max(0, Math.ceil(totalCount / pageSize) - 1)
    if (pageIndex > maxPage) {
      setPageIndex(maxPage)
    }
  }, [totalCount, pageSize, pageIndex])

  useEffect(() => {
    setPageIndex(0)
  }, [dateRange])

  const onSearchChange = useCallback((query: string) => {
    if (searchRef.current === query) {
      return
    }
    searchRef.current = query
    setSearch(query)
    setPageIndex(0)
  }, [])

  const handlePageSizeChange = useCallback((nextPageSize: number) => {
    setPageSize(nextPageSize)
    setPageIndex(0)
  }, [])

  const fetchAllMatchingInFlightRef = useRef<Promise<NotificationBlastSummary[]> | null>(null)

  const fetchAllMatchingMessages = useCallback(async (): Promise<NotificationBlastSummary[]> => {
    if (fetchAllMatchingInFlightRef.current) {
      return fetchAllMatchingInFlightRef.current
    }

    const fetchPromise = (async () => {
      const filterParams = buildFilterParams({ search, dateRange })
      const all: NotificationBlastSummary[] = []
      let offset = 0

      while (true) {
        const page = (await request({
          method: 'get',
          url: `/organizations/${organization.id}/notification-blasts-paged`,
          config: {
            queryStringParameters: {
              ...filterParams,
              limit: MESSAGES_FETCH_ALL_PAGE_SIZE,
              offset,
              sortBy: 'createdAt',
              sortDir: 'desc',
            },
          },
        })) as NotificationBlastSummary[]

        all.push(...page)
        if (page.length < MESSAGES_FETCH_ALL_PAGE_SIZE) {
          break
        }
        offset += MESSAGES_FETCH_ALL_PAGE_SIZE
      }

      return all
    })()

    fetchAllMatchingInFlightRef.current = fetchPromise

    try {
      return await fetchPromise
    } finally {
      if (fetchAllMatchingInFlightRef.current === fetchPromise) {
        fetchAllMatchingInFlightRef.current = null
      }
    }
  }, [dateRange, organization.id, request, search])

  return {
    messages,
    totalCount,
    pagedSummary,
    loading,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize: handlePageSizeChange,
    onSearchChange,
    refetch,
    refetchWithCount,
    filterKey,
    fetchAllMatchingMessages,
  }
}
